import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wheat, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const cls = 'w-full px-3 py-2.5 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none font-body text-sm';

export default function ZakatAktivasi({ onActivated }) {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [form, setForm] = useState({
    entity_name: settings?.mosque_name || '',
    nama_amil: user?.name || '',
    phone: '',
    alamat: '',
    logo_url: '',
    nilai_beras_per_jiwa: 2.5,
    harga_beras_per_kg: 15000,
    nominal_uang_per_jiwa: 37500,
    status: 'aktif',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };
    if (name === 'nilai_beras_per_jiwa' || name === 'harga_beras_per_kg') {
      updated.nominal_uang_per_jiwa =
        parseFloat(updated.nilai_beras_per_jiwa || 0) * parseFloat(updated.harga_beras_per_kg || 0);
    }
    setForm(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nama_amil.trim()) return toast.error('Nama Amil wajib diisi');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/zakat-ramadan/profile`, form, cfg);
      toast.success('Modul Zakat Ramadan berhasil diaktifkan!');
      onActivated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengaktifkan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-3xl p-8 text-white mb-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4">
          <Wheat className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-heading font-bold mb-2">Aktivasi Zakat Ramadan</h2>
        <p className="text-white/80 text-sm">
          Lengkapi data berikut untuk mengaktifkan modul pengumpulan zakat fitrah di masjid Anda.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Nama Masjid/Lembaga *</label>
          <input name="entity_name" value={form.entity_name} onChange={handleChange} className={cls}
            placeholder="Nama Masjid / UPZ" required />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Nama Amil/Penanggung Jawab *</label>
          <input name="nama_amil" value={form.nama_amil} onChange={handleChange} className={cls}
            placeholder="Nama lengkap amil" required />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Nomor HP</label>
          <input name="phone" value={form.phone} onChange={handleChange} className={cls}
            placeholder="08xxxxxxxxxx" type="tel" />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Alamat</label>
          <textarea name="alamat" value={form.alamat} onChange={handleChange} className={cls}
            rows={2} placeholder="Alamat masjid/UPZ" />
        </div>

        {/* Nilai Zakat */}
        <div className="bg-emerald-50 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-emerald-800">Besaran Zakat Fitrah</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-stone-500 mb-1">Beras/Jiwa (kg)</label>
              <input name="nilai_beras_per_jiwa" type="number" step="0.1" min="0.5" max="10"
                value={form.nilai_beras_per_jiwa} onChange={handleChange} className={cls} />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Harga Beras/kg (Rp)</label>
              <input name="harga_beras_per_kg" type="number" min="1000"
                value={form.harga_beras_per_kg} onChange={handleChange} className={cls} />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Setara Uang/Jiwa (Rp)</label>
              <input name="nominal_uang_per_jiwa" type="number" min="0"
                value={form.nominal_uang_per_jiwa} onChange={handleChange} className={cls} />
            </div>
          </div>
          <p className="text-xs text-emerald-700">
            Nilai default: 2,5 kg/jiwa × Rp 15.000 = Rp 37.500
          </p>
        </div>

        <button type="submit" disabled={saving} data-testid="aktivasi-zakat-btn"
          className="w-full py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? (
            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4" />
          )}
          {saving ? 'Menyimpan...' : 'Aktifkan Modul Zakat Ramadan'}
        </button>
      </form>
    </div>
  );
}
